import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Icon } from 'semantic-ui-react';
import { InputWrapper, AdvancedDatePickerWrapper } from 'react-lib/dist/components-es';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  dbFieldType,
  FILTER_EMPTY_BOOLEAN_PLACEHOLDER,
  FILTER_EMPTY_PLACEHOLDER,
  FILTER_NOT_EMPTY_PLACEHOLDER,
  filterFieldDataType,
  filterFieldSuffix,
  translationSuffixes,
} from '../../../constants';
import { filterFieldType } from '../../../types';
import CommonMultipleSearchSelectionWrapper from '../../common/CommonMultipleSearchSelectionWrapper';
import CommonTagsInputWrapper from '../../common/CommonTagsInputWrapper';
import { filterActions } from '../../../behavior/filter';
import i18n from '../../../i18n';
import CommonSelectInputWrapper from '../../common/CommonSelectInputWrapper';
import { isPrivateDataField } from '../../../utils';

const FiltersPanelInput = ({
  field,
  multipleSelectOptions,
  multipleSelectValues,
  updateMultipleSelectValues,
  formikProps,
  dataViewValues,
  showPersonalData,
  t,
}) => {
  const [isLoadedDataViewValues, setIsLoadedDataViewValues] = useState(false);

  useEffect(() => {
    if (JSON.stringify(formikProps.values) !== JSON.stringify(dataViewValues)) {
      multipleSelectValues = updateMultipleSelectValues(dataViewValues);
      formikProps.setValues(dataViewValues);
    }
    setIsLoadedDataViewValues(true);
  }, []);

  const renderCleanButton = fieldKey => (
    <Icon
      name="close"
      title={t('inventory.overview.page.filter.container.clean.icon')}
      onClick={() => {
        const valuesTmp = formikProps.values;
        if (multipleSelectValues[fieldKey] !== undefined) {
          multipleSelectValues[fieldKey] = [];
        }
        delete valuesTmp[fieldKey];
        formikProps.setValues(valuesTmp);
      }}
      style={{
        position: 'absolute',
        top:
          fieldKey.lastIndexOf(filterFieldSuffix.more_than) !== -1 ||
          fieldKey.lastIndexOf(filterFieldSuffix.less_than) !== -1
            ? '-14px'
            : '2px',
        right: '-4px',
        cursor: 'pointer',
        color: 'rgba(0, 149, 255, 0.15)',
      }}
    />
  );

  const setOrCleanUpMultipleSearchSelectionWrapper = (name, value) => {
    const valuesTmp = formikProps.values;
    valuesTmp[name] = value;
    if (valuesTmp[name].length === 0) {
      delete valuesTmp[name];
    }
    formikProps.setValues(valuesTmp);
  };

  const getDateValue = date => {
    if (date === null) {
      date = undefined;
    }
    return date ? moment(date) : undefined;
  };

  const hasValueNotEmpty = fieldKey =>
    formikProps.values[fieldKey] !== undefined &&
    formikProps.values[fieldKey] !== FILTER_EMPTY_PLACEHOLDER &&
    formikProps.values[fieldKey] !== FILTER_NOT_EMPTY_PLACEHOLDER &&
    formikProps.values[fieldKey] !== FILTER_EMPTY_BOOLEAN_PLACEHOLDER;

  const isEmpty = fieldKey =>
    formikProps.values[fieldKey] === FILTER_EMPTY_PLACEHOLDER ||
    formikProps.values[fieldKey] === FILTER_NOT_EMPTY_PLACEHOLDER;

  const replaceTagInputsPrivateData = (tag, fieldName) => {
    if (!tag.label) {
      return tag.value;
    }
    return !showPersonalData && isPrivateDataField(fieldName)
      ? tag.label.replace(/[a-z]/g, 'x')
      : tag.label;
  };

  const renderFields = () => {
    const fieldName = field.key;
    const unitValue =
      i18n.exists(field.label + translationSuffixes.unit) &&
      t(field.label + translationSuffixes.unit);

    switch (field.filterType.type) {
      case filterFieldDataType.date:
        return (
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column className="reducedRightPadding">
                <AdvancedDatePickerWrapper
                  {...formikProps}
                  setFieldValue={formikProps.setFieldValue}
                  value={
                    !isEmpty(fieldName + filterFieldSuffix.more_than)
                      ? getDateValue(formikProps.values[fieldName + filterFieldSuffix.more_than])
                      : ''
                  }
                  name={fieldName + filterFieldSuffix.more_than}
                  customLabel={t('filters.after')}
                  className={
                    hasValueNotEmpty(fieldName + filterFieldSuffix.more_than)
                      ? 'hasFilterValue inputWidthStretched reduced'
                      : 'inputWidthStretched reduced'
                  }
                />
                {hasValueNotEmpty(fieldName + filterFieldSuffix.more_than) &&
                  renderCleanButton(fieldName + filterFieldSuffix.more_than)}
              </Grid.Column>
              <Grid.Column className="reducedLeftPadding">
                <AdvancedDatePickerWrapper
                  {...formikProps}
                  setFieldValue={formikProps.setFieldValue}
                  value={
                    !isEmpty(fieldName + filterFieldSuffix.less_than)
                      ? getDateValue(formikProps.values[fieldName + filterFieldSuffix.less_than])
                      : ''
                  }
                  name={fieldName + filterFieldSuffix.less_than}
                  customLabel={t('filters.before')}
                  className={
                    hasValueNotEmpty(fieldName + filterFieldSuffix.less_than)
                      ? 'hasFilterValue inputWidthStretched'
                      : 'inputWidthStretched'
                  }
                />
                {hasValueNotEmpty(fieldName + filterFieldSuffix.less_than) &&
                  renderCleanButton(fieldName + filterFieldSuffix.less_than)}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );
      case filterFieldDataType.numeric:
        return (
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column className="reducedRightPadding">
                <div style={{ display: 'flex' }}>
                  <InputWrapper
                    {...formikProps}
                    id={fieldName + filterFieldSuffix.more_than}
                    name={fieldName + filterFieldSuffix.more_than}
                    value={
                      !isEmpty(fieldName + filterFieldSuffix.more_than)
                        ? formikProps.values[fieldName + filterFieldSuffix.more_than] || ''
                        : ''
                    }
                    onChange={() => {
                      formikProps.setValues(formikProps.values);
                    }}
                    customLabel={t('filters.between')}
                    inputClassName={
                      hasValueNotEmpty(fieldName + filterFieldSuffix.more_than)
                        ? 'hasFilterValue inputWidthStretched reduced'
                        : 'inputWidthStretched reduced'
                    }
                  />
                  {hasValueNotEmpty(fieldName + filterFieldSuffix.more_than) &&
                    renderCleanButton(fieldName + filterFieldSuffix.more_than)}
                </div>
              </Grid.Column>
              <Grid.Column className="reducedLeftPadding">
                <div style={{ display: 'flex' }}>
                  <InputWrapper
                    {...formikProps}
                    id={fieldName + filterFieldSuffix.less_than}
                    name={fieldName + filterFieldSuffix.less_than}
                    value={
                      !isEmpty(fieldName + filterFieldSuffix.less_than)
                        ? formikProps.values[fieldName + filterFieldSuffix.less_than] || ''
                        : ''
                    }
                    onChange={() => {
                      formikProps.setValues(formikProps.values);
                    }}
                    customLabel={t('filters.and')}
                    inputClassName={
                      hasValueNotEmpty(fieldName + filterFieldSuffix.less_than)
                        ? 'hasFilterValue inputWidthStretched'
                        : 'inputWidthStretched'
                    }
                    inputLabel={unitValue ? { basic: true, content: unitValue } : null}
                    inputLabelPosition={unitValue ? 'right' : null}
                  />
                  {hasValueNotEmpty(fieldName + filterFieldSuffix.less_than) &&
                    renderCleanButton(fieldName + filterFieldSuffix.less_than)}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );
      case filterFieldDataType.multiple_choice:
        return (
          multipleSelectOptions[fieldName] &&
          multipleSelectValues[fieldName] &&
          isLoadedDataViewValues && (
            <>
              <CommonMultipleSearchSelectionWrapper
                name={fieldName}
                optionsList={multipleSelectOptions[fieldName]}
                values={!isEmpty(fieldName) ? multipleSelectValues[fieldName] : []}
                onChange={value => setOrCleanUpMultipleSearchSelectionWrapper(fieldName, value)}
                inputClassName={hasValueNotEmpty(fieldName) ? 'hasFilterValue' : null}
              />
              {hasValueNotEmpty(fieldName) && renderCleanButton(fieldName)}
            </>
          )
        );
      case filterFieldDataType.multiple_auto_complete:
      case filterFieldDataType.multiple_auto_complete_or_text_contains:
      case filterFieldDataType.multiple_auto_complete_contains:
        const isEmptyValue =
          !hasValueNotEmpty(fieldName) ||
          !formikProps.values[fieldName] ||
          formikProps.values[fieldName].length === 0;

        if (formikProps.values[fieldName] && !isEmpty(fieldName)) {
          formikProps.values[fieldName].forEach(
            tag => (tag.label = replaceTagInputsPrivateData(tag, fieldName))
          );
        }

        return (
          <>
            <CommonTagsInputWrapper
              {...formikProps}
              setFieldValue={formikProps.setFieldValue}
              dynamicSuggestionListActionWithName={filterActions.getAutoCompleteSuggestionsList.bind(
                this,
                showPersonalData
              )}
              name={fieldName}
              matchProperty="value"
              displayProps="label"
              value={!isEmpty(fieldName) && formikProps?.values[fieldName]}
              maxTags={10}
              onlyUnique
              className={
                !isEmptyValue ? 'hasFilterValue inputWhiteBackGround' : 'inputWhiteBackGround'
              }
              onChange={() => {
                formikProps.setValues(formikProps.values);
              }}
              onlyAutocomplete={
                field.filterType.type !==
                filterFieldDataType.multiple_auto_complete_or_text_contains
              }
            />
            {!isEmptyValue && renderCleanButton(fieldName)}
          </>
        );
      case filterFieldDataType.dropdown:
        return (
          <>
            <CommonSelectInputWrapper
              {...formikProps}
              setFieldValue={formikProps.setFieldValue}
              name={fieldName}
              value={formikProps.values[fieldName]}
              options={multipleSelectOptions}
              onChange={(e, value) => {
                formikProps.setValue(fieldName, value);
              }}
            />
            {hasValueNotEmpty(fieldName) && renderCleanButton(fieldName)}
          </>
        );
      case filterFieldDataType.boolean_dropdown:
        const options = [
          { text: t(dbFieldType.none), value: dbFieldType.none },
          { text: t(dbFieldType.true), value: dbFieldType.true },
          { text: t(dbFieldType.false), value: dbFieldType.false },
        ];
        return (
          <>
            <CommonSelectInputWrapper
              name={fieldName}
              value={formikProps.values[fieldName] || ''}
              options={options}
              onChange={(e, newValue) => formikProps.setFieldValue(fieldName, newValue)}
              className={hasValueNotEmpty(fieldName) ? 'hasFilterValue' : ''}
            />
            {hasValueNotEmpty(fieldName) && renderCleanButton(fieldName)}
          </>
        );
      case filterFieldDataType.boolean:
        return (
          <InputWrapper
            {...formikProps}
            value={!isEmpty(fieldName) ? formikProps.values[fieldName] || '' : ''}
            onChange={() => {
              formikProps.setValues(formikProps.values);
            }}
            name={fieldName}
            type="CHECKBOX"
            toggle
          />
        );
      default:
        return (
          <>
            <InputWrapper
              {...formikProps}
              value={!isEmpty(fieldName) ? formikProps.values[fieldName] || '' : ''}
              onChange={() => {
                formikProps.setValues(formikProps.values);
              }}
              name={fieldName}
              inputClassName={hasValueNotEmpty(fieldName) ? 'hasFilterValue' : ''}
            />
            {hasValueNotEmpty(fieldName) && renderCleanButton(fieldName)}
          </>
        );
    }
  };

  return renderFields();
};

FiltersPanelInput.propTypes = {
  field: filterFieldType.isRequired,
  multipleSelectOptions: PropTypes.object.isRequired,
  multipleSelectValues: PropTypes.object.isRequired,
  formikProps: PropTypes.object.isRequired,
  dataViewValues: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

export default compose(withTranslation(), connect(mapStateToProps, null))(FiltersPanelInput);
